import { cn } from '@/lib/utils';
import { ArrowUpRight } from 'lucide-react';
import { useRouter } from 'next/router';
import { Icons } from '../ui';

type IntegrationCardProps = {
  title: string;
  description: string;
  icon: keyof typeof Icons | never;
  actionLabel: string;
  onClick?: () => void;
  className?: string;
  link?: string;
  isDisabled?: boolean;
  badge?: string;
};

const IntegrationCard = ({
  title,
  description,
  icon,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  actionLabel,
  onClick,
  className,
  link,
  isDisabled = false,
  badge,
}: IntegrationCardProps) => {
  const Icon = Icons[icon];
  const router = useRouter();

  const handleClick = () => {
    if (isDisabled) return;
    if (onClick) onClick();
    if (link) router.push(link);
  };

  return (
    <button
      onClick={handleClick}
      disabled={isDisabled}
      className={cn(
        'group relative flex w-full flex-col rounded-lg border p-4 text-left transition-all duration-150',
        isDisabled
          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
          : 'bg-white hover:cursor-pointer hover:bg-gray-50 hover:border-primary',
        className
      )}
    >
      {!isDisabled && (
        <ArrowUpRight className="absolute top-3 right-3 size-4 text-primary opacity-0 transition-opacity duration-150 group-hover:opacity-100" />
      )}

      <div className="space-y-3">
        <div className="size-8">
          <Icon className="h-full w-full text-text-secondary" />
        </div>

        <div className="space-y-1">
          <h3 className="text-sm font-medium leading-5 text-text-primary">
            {title}
            {badge && (
              <span className="ml-2 inline-flex items-center rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                {badge}
              </span>
            )}
          </h3>
          <p className="text-xs leading-4 text-[#4B5563]">{description}</p>
        </div>
      </div>
    </button>
  );
};

export { IntegrationCard };
